import { Alert, Button, Field, Input } from '@marrafa-gioale/gymx-ui-web'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { MailCheck } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ApiError, errorMessage } from '@/api/errors'
import {
  acceptPendingInvitation,
  beginInvitationLogin,
  beginInvitationRegistration,
  cancelPendingInvitation,
  completeInvitationMfa,
  getSnapshot,
} from '@/auth/session-store'
import { Logo } from '@/components/layout/logo'

/**
 * Accettare un invito — OPS-02, contract `1.0.0-draft.12`.
 *
 * ## Perché questa pagina non esisteva, ed è la cosa da capire prima del codice
 *
 * Il pannello sapeva **creare** e **revocare** un invito da mesi, e non redimerne uno:
 * l'email arrivava con un token e non c'era nessun posto in cui spenderlo. Cioè una
 * palestra non poteva avere un secondo operatore, e sette fasi di prodotto sono state
 * costruite sopra quella possibilità. Vedi §8bis, «Ventidue rotte che nessuno può
 * raggiungere».
 *
 * ## L'ordine dei tre passi è l'unica cosa che poteva funzionare
 *
 * `applyLoginSession` respinge chi non lavora in nessuna palestra — è il gate del 19
 * agosto, ed è giusto. Ma chi accetta il **primo** invito è esattamente quello. Se
 * accettasse dopo il login non arriverebbe mai al login; se il gate cadesse per far
 * passare questo caso, tornerebbe il difetto che quel gate ha chiuso.
 *
 * Quindi: **autentica, accetta, applica**. L'invito è ciò che rende quella sessione una
 * sessione da pannello, e va speso prima che il gate la guardi. Sta in
 * `session-store.ts`, dove le tre fasi sono una funzione ciascuna.
 *
 * ## Il token sta nell'URL e non si chiede a mano
 *
 * `?token=…` è ciò che il link dell'email porta. Non c'è un campo in cui incollarlo:
 * chi arriva qui senza token non ha un invito da accettare, e offrirgli una casella
 * significherebbe invitarlo a indovinare.
 *
 * ## Un solo messaggio per quattro fallimenti
 *
 * Token inesistente, già speso, revocato e scaduto sono lo stesso `401`, ed è
 * deliberato nel contract: distinguerli direbbe a chi prova token a caso quali
 * esistono. La pagina mostra quel messaggio così com'è — viene dal server, in
 * italiano, e correggerlo non richiede un rilascio del pannello.
 */
const searchSchema = z.object({
  token: z.string().optional(),
})

export const Route = createFileRoute('/invito')({
  validateSearch: searchSchema,
  component: InvitationPage,
})

const credentialsSchema = z.object({
  email: z.string().trim().min(1, 'Inserisci la tua email.').email('Indirizzo email non valido.'),
  password: z.string().min(1, 'Inserisci la password.'),
})
type CredentialsValues = z.infer<typeof credentialsSchema>

/**
 * La registrazione chiede **anche il nome**, e non è un campo in più per gentilezza: è
 * il nome che comparirà accanto a ogni azione nel registro di audit, e chi lo legge un
 * anno dopo cerca una persona da chiamare. Chiederlo dopo significa non averlo mai.
 *
 * Otto caratteri di minimo come sul login: l'autorità resta il server, che rifiuta anche
 * le password in denylist con `422 password_too_common` — cosa che il client non sa.
 */
const registrationSchema = z.object({
  name: z.string().trim().min(1, 'Inserisci il tuo nome.'),
  email: z.string().trim().min(1, 'Inserisci la tua email.').email('Indirizzo email non valido.'),
  password: z.string().min(8, 'Almeno 8 caratteri.'),
})
type RegistrationValues = z.infer<typeof registrationSchema>

const codeSchema = z.object({
  code: z.string().trim().min(6, 'Il codice ha sei cifre.'),
})
type CodeValues = z.infer<typeof codeSchema>

type Step = 'credentials' | 'register' | 'mfa' | 'accepting' | 'done'

function InvitationPage() {
  const { token } = Route.useSearch()
  const navigate = useNavigate()

  // Chi ha già una sessione aperta salta il login: lavora già in una palestra ed è
  // stato invitato in una seconda, che è un caso normale per un personal trainer.
  const alreadyIn = getSnapshot().state.status === 'authenticated'

  const [step, setStep] = useState<Step>(alreadyIn ? 'accepting' : 'credentials')
  const [error, setError] = useState<string | null>(null)
  const [joined, setJoined] = useState<{ organizationId: string; role: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const credentials = useForm<CredentialsValues>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: '', password: '' },
  })
  const codeForm = useForm<CodeValues>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: '' },
  })
  const registration = useForm<RegistrationValues>({
    resolver: zodResolver(registrationSchema),
    defaultValues: { name: '', email: '', password: '' },
  })

  async function accept() {
    if (token === undefined) return
    setBusy(true)
    setError(null)
    try {
      const result = await acceptPendingInvitation(token)
      setJoined(result)
      setStep('done')
    } catch (caught) {
      // Il messaggio arriva dal server e si mostra com'è: è in italiano, e distingue
      // «accesso richiesto» da «invito non più valido» con le parole invece che con il
      // codice — che per entrambi è `401`.
      setError(
        caught instanceof ApiError
          ? errorMessage(caught)
          : 'Non è stato possibile accettare l’invito.',
      )
      setStep(alreadyIn ? 'accepting' : 'credentials')
    } finally {
      setBusy(false)
    }
  }

  async function onCredentials(values: CredentialsValues) {
    setBusy(true)
    setError(null)
    try {
      const result = await beginInvitationLogin(values.email, values.password)
      if (result.kind === 'mfa_required') {
        setStep('mfa')
        return
      }
      await accept()
    } catch (caught) {
      setError(caught instanceof ApiError ? errorMessage(caught) : 'Accesso non riuscito.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Chi non ha un account: si registra **e** accetta, nello stesso gesto.
   *
   * `POST /auth/register` restituisce già una sessione, che resta in attesa come quella
   * del login: un account appena nato non appartiene a nessuna palestra, ed è
   * esattamente ciò che il gate respinge. L'invito è ciò che la rende valida, quindi va
   * speso subito dopo — e se fallisce, la sessione appena creata viene revocata invece di
   * restare viva per trenta giorni.
   */
  async function onRegister(values: RegistrationValues) {
    setBusy(true)
    setError(null)
    try {
      await beginInvitationRegistration({
        email: values.email,
        password: values.password,
        name: values.name,
      })
      await accept()
    } catch (caught) {
      setError(caught instanceof ApiError ? errorMessage(caught) : 'Registrazione non riuscita.')
      setStep('register')
    } finally {
      setBusy(false)
    }
  }

  async function onCode(values: CodeValues) {
    setBusy(true)
    setError(null)
    try {
      await completeInvitationMfa({ code: values.code })
      await accept()
    } catch (caught) {
      setError(caught instanceof ApiError ? errorMessage(caught) : 'Codice non valido.')
    } finally {
      setBusy(false)
    }
  }

  function abandon() {
    // Una sessione autenticata e mai applicata non va lasciata viva per trenta giorni
    // solo perché qualcuno ha chiuso la pagina.
    cancelPendingInvitation()
    void navigate({ to: '/login' })
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center gap-6 p-6">
      <div className="flex flex-col items-center gap-2">
        <Logo />
        <h1 className="text-lg font-semibold">Invito a una palestra</h1>
      </div>

      {token === undefined ? (
        // Nessun campo in cui incollarlo: chi arriva senza token non ha un invito, e
        // offrirgli una casella significherebbe invitarlo a indovinare.
        <Alert variant="warning">
          Questo link non contiene un invito. Apri il collegamento che hai ricevuto per email, per
          intero.
        </Alert>
      ) : null}

      {error === null ? null : <Alert variant="danger">{error}</Alert>}

      {step === 'done' && joined !== null ? (
        <div className="flex flex-col gap-4">
          <Alert variant="success">
            <MailCheck className="size-4" /> Sei entrato nella palestra come{' '}
            <strong>{joined.role}</strong>.
          </Alert>
          <Button onClick={() => void navigate({ to: '/' })}>Apri il pannello</Button>
        </div>
      ) : null}

      {token !== undefined && step === 'credentials' ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => void credentials.handleSubmit(onCredentials)(event)}
        >
          <p className="text-muted-foreground text-sm">
            Accedi con il tuo account per accettare l’invito.
          </p>
          <Field label="Email" required error={credentials.formState.errors.email?.message}>
            {(props) => (
              <Input
                {...props}
                {...credentials.register('email')}
                type="email"
                autoComplete="username"
                inputMode="email"
                autoFocus
              />
            )}
          </Field>
          <Field label="Password" required error={credentials.formState.errors.password?.message}>
            {(props) => (
              <Input
                {...props}
                {...credentials.register('password')}
                type="password"
                autoComplete="current-password"
              />
            )}
          </Field>
          <Button type="submit" disabled={busy}>
            {busy ? 'Un momento…' : 'Accetta l’invito'}
          </Button>
          {/* Prima diceva «registrati dall'app e poi torna su questo link»: era
              un'istruzione a fare un giro fuori dal pannello per un passaggio che il
              contract sa fare, e chi riceve il primo invito della sua vita è
              esattamente chi non ha l'app. */}
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground text-xs underline underline-offset-4"
            onClick={() => {
              setError(null)
              setStep('register')
            }}
          >
            Non ho ancora un account
          </button>
        </form>
      ) : null}

      {token !== undefined && step === 'register' ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => void registration.handleSubmit(onRegister)(event)}
        >
          <p className="text-muted-foreground text-sm">
            Crea l’account e accetta l’invito in un colpo solo.
          </p>
          <Field
            label="Nome e cognome"
            required
            error={registration.formState.errors.name?.message}
          >
            {(props) => (
              <Input {...props} {...registration.register('name')} autoComplete="name" autoFocus />
            )}
          </Field>
          <Field label="Email" required error={registration.formState.errors.email?.message}>
            {(props) => (
              <Input
                {...props}
                {...registration.register('email')}
                type="email"
                autoComplete="username"
                inputMode="email"
              />
            )}
          </Field>
          <Field
            label="Password"
            required
            hint="Almeno 8 caratteri."
            error={registration.formState.errors.password?.message}
          >
            {(props) => (
              <Input
                {...props}
                {...registration.register('password')}
                type="password"
                autoComplete="new-password"
              />
            )}
          </Field>
          <Button type="submit" disabled={busy}>
            {busy ? 'Un momento…' : 'Crea l’account e accetta'}
          </Button>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground text-xs underline underline-offset-4"
            onClick={() => {
              setError(null)
              setStep('credentials')
            }}
          >
            Ho già un account
          </button>
        </form>
      ) : null}

      {token !== undefined && step === 'mfa' ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => void codeForm.handleSubmit(onCode)(event)}
        >
          <p className="text-muted-foreground text-sm">
            Il tuo account ha il secondo fattore: inserisci il codice.
          </p>
          <Field label="Codice" required error={codeForm.formState.errors.code?.message}>
            {(props) => (
              <Input
                {...props}
                {...codeForm.register('code')}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
              />
            )}
          </Field>
          <Button type="submit" disabled={busy}>
            {busy ? 'Un momento…' : 'Conferma'}
          </Button>
        </form>
      ) : null}

      {token !== undefined && step === 'accepting' && alreadyIn ? (
        <div className="flex flex-col gap-4">
          <p className="text-muted-foreground text-sm">
            Sei già dentro con un altro account o in un’altra palestra. Accetta l’invito per
            aggiungere questa.
          </p>
          <Button onClick={() => void accept()} disabled={busy}>
            {busy ? 'Un momento…' : 'Accetta l’invito'}
          </Button>
        </div>
      ) : null}

      {step === 'done' ? null : (
        <button className="text-muted-foreground text-xs underline" onClick={abandon} type="button">
          Torna all’accesso
        </button>
      )}
    </div>
  )
}
